import { Helmet } from "react-helmet-async";
import { top150USCities } from "./usCitiesData";
import { useStoreReviews } from "@/hooks/useStoreReviews";

const SOCIAL_LINKS = [
  "https://www.instagram.com/hijabistore.official/",
  "https://www.facebook.com/people/Hijabi-Store/61585983490843/",
  "https://www.pinterest.com/hijabistoreofficial/",
  "https://www.tiktok.com/@hijabistoreofficial",
];

const LocalBusinessSchema = () => {
  const { totalReviews, avgRating, recentReviews } = useStoreReviews();

  const hasReviews = totalReviews > 0;

  // Build aggregate rating object (reused across schemas)
  const aggregateRating = hasReviews
    ? {
        "@type": "AggregateRating",
        ratingValue: String(avgRating),
        reviewCount: totalReviews,
        bestRating: "5",
        worstRating: "1",
      }
    : undefined;

  // Main organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "HijabiStore",
    url: "https://hijabistore.com",
    logo: "https://hijabistore.com/logo.png",
    description:
      "HijabiStore is a curated marketplace for modest fashion, featuring hijabs, abayas, and modest dresses from independent vendors worldwide.",
    sameAs: SOCIAL_LINKS,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "support@hijabistore.com",
      availableLanguage: ["English"],
    },
  };

  // Online store schema
  const onlineStoreSchema = {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    name: "HijabiStore",
    url: "https://hijabistore.com",
    description:
      "Shop modest fashion online at HijabiStore. Free shipping on U.S. orders over $75. Hijabs, abayas, modest dresses, and more from independent designers.",
    priceRange: "$$",
    currenciesAccepted: "USD",
    paymentAccepted: "Credit Card, Debit Card, Apple Pay",
    image: "https://hijabistore.com/og-image.jpg",
    telephone: "",
    email: "support@hijabistore.com",
    ...(aggregateRating ? { aggregateRating } : {}),
    areaServed: [
      { "@type": "Country", name: "United States" },
      ...top150USCities.map((location) => ({
        "@type": "City",
        name: location.city,
        containedInPlace: {
          "@type": "State",
          name: location.state,
          containedInPlace: { "@type": "Country", name: "United States" },
        },
      })),
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Modest Fashion Collection",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "Hijabs",
          description:
            "Premium hijabs in various fabrics including chiffon, jersey, silk, and cotton",
        },
        {
          "@type": "OfferCatalog",
          name: "Abayas",
          description: "Elegant abayas in modern and traditional styles",
        },
        {
          "@type": "OfferCatalog",
          name: "Modest Dresses",
          description:
            "Beautiful modest dresses for everyday and special occasions",
        },
        {
          "@type": "OfferCatalog",
          name: "Prayer Wear",
          description: "Comfortable and stylish prayer garments",
        },
      ],
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://hijabistore.com/shop?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
    offers: {
      "@type": "Offer",
      description: "Free shipping on orders over $75",
      priceSpecification: {
        "@type": "DeliveryChargeSpecification",
        appliesToDeliveryMethod:
          "http://purl.org/goodrelations/v1#DeliveryModeDirectDownload",
        eligibleTransactionVolume: {
          "@type": "PriceSpecification",
          minPrice: 75,
          priceCurrency: "USD",
        },
        price: 0,
        priceCurrency: "USD",
      },
    },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: 5.99,
        currency: "USD",
      },
      shippingDestination: {
        "@type": "DefinedRegion",
        addressCountry: "US",
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: {
          "@type": "QuantitativeValue",
          minValue: 1,
          maxValue: 2,
          unitCode: "DAY",
        },
        transitTime: {
          "@type": "QuantitativeValue",
          minValue: 5,
          maxValue: 7,
          unitCode: "DAY",
        },
      },
    },
    returnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "US",
      returnPolicyCategory:
        "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 30,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
      returnPolicySeasonalOverride: {
        "@type": "MerchantReturnPolicySeasonalOverride",
        merchantReturnDays: 30,
        startDate: "2025-11-01",
        endDate: "2026-01-31",
      },
    },
  };

  // Individual review snippets (up to 5)
  const reviewSchemas = recentReviews.map((review) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "LocalBusiness",
      name: "HijabiStore",
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: String(review.rating),
      bestRating: "5",
      worstRating: "1",
    },
    author: {
      "@type": "Person",
      name: review.customerName || "Verified Customer",
    },
    datePublished: review.created_at.split("T")[0],
    ...(review.title ? { name: review.title } : {}),
    ...(review.content ? { reviewBody: review.content } : {}),
  }));

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(onlineStoreSchema)}
      </script>
      {reviewSchemas.map((schema, index) => (
        <script key={`rev-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default LocalBusinessSchema;
